import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /**
   * What to draw instead of the full-page report.
   *
   * For a boundary around a PART of a page (RFC 0297 §5a's embed): the default
   * report is the right answer when the thing that failed was the whole view,
   * and the wrong one inside a 420px box in the middle of somebody's prose.
   */
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo })
    if (import.meta.env.DEV) {
      const componentStack = errorInfo?.componentStack || ''
      const componentStackLines = componentStack
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
      console.error('[ErrorBoundary] Caught render error', {
        message: error?.message,
        stack: error?.stack,
        componentStack,
        componentStackLines,
      })
      ;(window as any).__lastRenderError = {
        message: error?.message,
        stack: error?.stack,
        componentStack,
        componentStackLines,
        at: new Date().toISOString(),
      }
      console.error(
        '[ErrorBoundary] Component stack lines',
        componentStackLines,
      )
    }
    // TODO: Integrate with error reporting service (Sentry, LogRocket, etc.)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) return this.props.fallback
      const errorMessage =
        (this.state.error as any)?.message ||
        String(this.state.error || 'Unknown error')
      const errorStack = (this.state.error as any)?.stack || ''
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center space-y-6">
            {/* @spec REQ-INFRA-UI-2.7 User-friendly error message */}
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">
                Something went wrong
              </h1>
              <p className="text-muted-foreground">
                We apologize for the inconvenience. Please try refreshing the
                page.
              </p>
            </div>

            {/* @spec REQ-INFRA-UI-2.8 Error details in development mode */}
            {import.meta.env.DEV && this.state.error && (
              <details className="text-left bg-card border border-border rounded-lg p-4">
                <summary className="cursor-pointer font-medium text-sm text-card-foreground">
                  Error Details (Development)
                </summary>
                <div className="mt-3 space-y-2 text-xs font-mono text-card-foreground">
                  <div>
                    <strong>Error:</strong> {errorMessage}
                  </div>
                  {errorStack && (
                    <div>
                      <strong>Stack:</strong>
                      <pre className="mt-1 text-xs overflow-auto">
                        {errorStack}
                      </pre>
                    </div>
                  )}
                  {this.state.errorInfo && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="mt-1 text-xs overflow-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* @spec REQ-INFRA-UI-2.9 Recovery actions */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="w-full bg-brand-400 hover:bg-brand-500 text-white font-medium py-2 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
              >
                Refresh Page
              </button>

              <button
                type="button"
                onClick={() => {
                  this.setState({
                    hasError: false,
                    error: undefined,
                    errorInfo: undefined,
                  })
                }}
                className="w-full bg-secondary border border-border hover:bg-secondary/80 text-secondary-foreground font-medium py-2 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-ring"
              >
                Try Again
              </button>
            </div>

            {/* @spec REQ-INFRA-UI-2.10 Support information */}
            <div className="text-sm text-muted-foreground">
              If this problem persists, please contact support.
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
